import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, ImagePlus } from 'lucide-react';

async function fileToDataUrl(file, maxW = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(1, maxW / img.width);
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function ImageUpload({ value, onChange, label = 'Immagine', testId = 'image-upload', square = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file, 1600);
      onChange(dataUrl);
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  return (
    <div className="space-y-2">
      <label className="text-sm text-[color:var(--text-muted)] block">{label}</label>
      <div className={`relative rounded-lg border border-dashed border-[color:var(--border-c)] bg-[color:var(--surface)] overflow-hidden ${square ? 'aspect-square w-32' : 'aspect-[16/9]'} flex items-center justify-center`}>
        {value ? (
          <>
            <img src={value} alt={label} className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange('')} data-testid={`${testId}-remove`} className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white p-1.5 rounded-full"><X className="w-3 h-3" /></button>
          </>
        ) : (
          <div className="text-center text-[color:var(--text-muted)] p-4">
            <ImagePlus className="w-8 h-8 mx-auto mb-2 opacity-60" />
            <p className="text-xs">JPEG / PNG</p>
          </div>
        )}
      </div>
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testId}-button`} className="w-full">
        <Upload className="w-4 h-4 mr-2" /> {busy ? 'Caricamento…' : (value ? 'Cambia' : 'Carica')}
      </Button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} />
    </div>
  );
}

export function GalleryUpload({ value = [], onChange, testId = 'gallery-upload' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const dataUrls = await Promise.all(files.slice(0, 8).map((f) => fileToDataUrl(f, 1400, 0.8)));
      onChange([...(value || []), ...dataUrls]);
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <label className="text-sm text-[color:var(--text-muted)] block">Gallery (multi)</label>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((src, i) => (
          <div key={src} className="relative aspect-square rounded-md overflow-hidden border border-[color:var(--border-c)] bg-[color:var(--surface)]">
            <img src={src} alt={`g${i}`} className="w-full h-full object-cover" />
            <button type="button" onClick={() => remove(i)} data-testid={`${testId}-remove-${i}`} className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white p-1 rounded-full"><X className="w-3 h-3" /></button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testId}-add`} className="aspect-square rounded-md border border-dashed border-[color:var(--border-c)] bg-[color:var(--surface)] flex flex-col items-center justify-center text-[color:var(--text-muted)] hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]">
          <ImagePlus className="w-5 h-5 mb-1" />
          <span className="text-xs">{busy ? '…' : 'Aggiungi'}</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handle} />
    </div>
  );
}
